/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { DashboardType } from 'jotai/dashboard';
import { createEntity } from 'jotai/entity';
import { RunType, SanctioningBodyEnum, WorkspaceInfo } from 'models/viewModels';
import { lockedWorkspaceService } from 'services/workspace/LockedWorkspaceService';
import { systemWorkspaceService } from 'services/workspace/SystemWorkspaceService';
import { LockedWorkspaceId } from './LockedWorkspaces';
import { SystemWorkspaceId } from './SystemWorkspaceId';
import { WorkspaceObject } from './WorkspaceObject';

export const getWorkspaceDefault = (
  id: string,
  dashboardType: DashboardType = DashboardType.Default
): WorkspaceObject => {
  let workspace: WorkspaceObject;
  switch (dashboardType) {
    case DashboardType.CombinedView:
      workspace = lockedWorkspaceService.getWorkspace(LockedWorkspaceId.CombinedView);
      break;
    case DashboardType.TrackTemperature:
      workspace = lockedWorkspaceService.getWorkspace(LockedWorkspaceId.TrackTemperature);
      break;
    case DashboardType.IndyCar:
      workspace = lockedWorkspaceService.getWorkspace(LockedWorkspaceId.IndyCar);
      break;
    default:
      workspace = systemWorkspaceService.getWorkspace(SystemWorkspaceId.Blank);
      break;
  }
  workspace.id = id;
  return workspace;
};

export const getDefaultSystemWorkspaceId = (sessionType: RunType): SystemWorkspaceId => {
  switch (sessionType) {
    case RunType.PRACTICE:
      return SystemWorkspaceId.DefaultPractice;
    case RunType.QUALIFYING:
      return SystemWorkspaceId.DefaultQualifying;
    case RunType.RACE:
      return SystemWorkspaceId.DefaultRace;
    default:
      return SystemWorkspaceId.Blank;
  }
};

export const getDefaultSystemWorkspaceSlug = (sessionType: RunType): string => {
  return systemWorkspaceService.getWorkspace(getDefaultSystemWorkspaceId(sessionType)).slug;
};

export const getBlankWorkspace = (id: string, name = '', owner = ''): WorkspaceObject => {
  return structuredClone({
    id,
    name,
    owner,
    activeView: 't1',
    views: {
      byId: {
        t1: { id: 't1', name: 'Primary View', visualizations: [], layouts: 'vl1' },
      },
      allIds: ['t1'],
    },
    viewLayouts: {
      byId: {
        vl1: [],
      },
      allIds: ['vl1'],
    },
    visualizations: {
      byId: {},
      allIds: [],
    },
    visualizationSettings: {
      byId: {},
      allIds: [],
    },
    selectedCompetitorGroups: {},
    competitorGroups: createEntity(),
    competitorGroupVehicles: createEntity(),
    selectedReferenceVehicle: createEntity(),
    hideFooter: false,
    trackMapSettings: createEntity(),
    slug: '',
  });
};

export const upgrade = (workspace: WorkspaceObject): WorkspaceObject => {
  if (!Object.hasOwn(workspace, 'selectedCompetitorGroups')) {
    workspace.selectedCompetitorGroups = {};
  }
  if (!Object.hasOwn(workspace, 'competitorGroups')) {
    workspace.competitorGroups = createEntity();
  }
  if (!Object.hasOwn(workspace, 'competitorGroupVehicles')) {
    workspace.competitorGroupVehicles = createEntity();
  }
  if (!Object.hasOwn(workspace, 'selectedReferenceVehicle')) {
    workspace.selectedReferenceVehicle = createEntity();
  }
  if (!Object.hasOwn(workspace, 'trackMapSettings')) {
    workspace.trackMapSettings = createEntity();
  }
  return workspace;
};

export const getDefaultSystemWorkspaceIdBySanctioningBody = (
  sanctioningBody: SanctioningBodyEnum
): SystemWorkspaceId => {
  switch (sanctioningBody) {
    case SanctioningBodyEnum.NASCAR:
      return SystemWorkspaceId.DefaultRace;
    case SanctioningBodyEnum.INDYCAR:
      return SystemWorkspaceId.DefaultRaceIndy;
    default:
      return SystemWorkspaceId.Blank;
  }
};

export const getBlankWorkspaceInfo = (id: string): WorkspaceInfo => {
  return {
    id: id,
    name: '',
    slug: '',
    owner: '',
    sanctioningBody: SanctioningBodyEnum.UNKNOWN,
    shared: false,
    sharedDomains: [],
  };
};

export const getDefaultSystemWorkspaceBySanctioningBodySlug = (sanctioningBody: SanctioningBodyEnum): string => {
  return systemWorkspaceService.getWorkspace(getDefaultSystemWorkspaceIdBySanctioningBody(sanctioningBody)).slug;
};
